import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, InlineLoading } from "@carbon/react";
import { Download } from "@carbon/icons-react";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { useOcPdf } from "../../../hooks/useOcActions";

interface OcDocumentTabProps {
  ocId: string;
  ocCode: string;
  /** True once the Documento tab is selected — the PDF is not fetched before that. */
  active: boolean;
}

/** The OC's PDF rendered inline (native `<iframe>` on a blob URL) plus a download button. */
const OcDocumentTab = ({ ocId, ocCode, active }: OcDocumentTabProps) => {
  const { t } = useTranslation();
  const { data: blob, isLoading, isError, refetch } = useOcPdf(ocId, active);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setBlobUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (isError) {
    return (
      <EmptyState
        title={t("purchaseOrders.detail.document.loadError")}
        action={
          <Button kind="tertiary" onClick={() => refetch()}>
            {t("purchaseOrders.detail.document.retry")}
          </Button>
        }
      />
    );
  }

  if (isLoading || !blobUrl) {
    return (
      <InlineLoading
        description={t("purchaseOrders.detail.document.loading")}
      />
    );
  }

  const download = () => {
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${ocCode}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="oc-detail__document">
      <div className="oc-detail__document-bar">
        <Button kind="tertiary" renderIcon={Download} onClick={download}>
          {t("purchaseOrders.detail.document.download")}
        </Button>
      </div>
      <iframe
        className="oc-detail__document-frame"
        title={t("purchaseOrders.detail.document.viewerTitle")}
        src={blobUrl}
      />
    </div>
  );
};

export default OcDocumentTab;
